/**
 * Multi-signal private/incognito browsing detection — pure JS, no server
 * calls, no IP tracking. We combine 4 independent signals and return a
 * confidence score; >= 2 hits = "almost certainly private".
 *
 * Signals (works across Chrome, Edge, Safari, Firefox, Brave):
 *  1. Storage-quota estimate — incognito caps drastically (<= 120MB Chromium,
 *     ~1.1GB in Safari) compared to multi-GB in normal mode.
 *  2. Persistent-storage permission — always false / unavailable in private.
 *  3. IndexedDB open-and-write — throws or hangs in some private modes.
 *  4. localStorage round-trip + read-back — Firefox PB and some Safari modes
 *     either disable it or return empty after a write.
 *
 * Each check is fail-soft (a thrown error counts as a positive signal).
 */
const TIMEOUT_MS = 1500;

function withTimeout(promise, ms = TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), ms),
    ),
  ]);
}

async function checkStorageQuota() {
  if (!navigator.storage || !navigator.storage.estimate) return false;
  try {
    const { quota } = await withTimeout(navigator.storage.estimate());
    if (!quota) return false;
    // Chromium-based browsers cap incognito quota to <= 120MB.
    // Firefox PB / Safari private mode also report much lower than normal mode.
    return quota < 120 * 1024 * 1024;
  } catch {
    return true;
  }
}

async function checkPersistedStorage() {
  if (!navigator.storage || !navigator.storage.persisted) return false;
  try {
    const persisted = await withTimeout(navigator.storage.persisted());
    // Private modes never grant persistent storage.
    return persisted === false && !!navigator.storage.persist;
  } catch {
    return false;
  }
}

async function checkIndexedDB() {
  if (!window.indexedDB) return true; // missing = likely private
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };
    setTimeout(() => finish(true), TIMEOUT_MS);
    try {
      const req = window.indexedDB.open("__decal_lab_idb_probe", 1);
      req.onerror = () => finish(true);
      req.onsuccess = () => {
        try {
          req.result.close();
          window.indexedDB.deleteDatabase("__decal_lab_idb_probe");
        } catch {
          // ignore
        }
        finish(false);
      };
    } catch {
      finish(true);
    }
  });
}

function checkLocalStorage() {
  try {
    const key = "__decal_lab_ls_probe";
    const v = `t-${Date.now()}`;
    localStorage.setItem(key, v);
    const got = localStorage.getItem(key);
    localStorage.removeItem(key);
    return got !== v;
  } catch {
    return true;
  }
}

/**
 * @returns {Promise<{isPrivate: boolean, score: number, signals: object}>}
 */
export async function detectIncognito() {
  const [quotaLow, persistDenied, idbBlocked] = await Promise.all([
    checkStorageQuota(),
    checkPersistedStorage(),
    checkIndexedDB(),
  ]);
  const lsBlocked = checkLocalStorage();

  const signals = {
    quotaLow,
    persistDenied,
    idbBlocked,
    lsBlocked,
  };
  const score = Object.values(signals).filter(Boolean).length;
  // Two or more positive signals → call it private. One alone (esp. quotaLow)
  // can trigger on small-disk devices, so we don't want a single-signal alarm.
  return { isPrivate: score >= 2, score, signals };
}

const DISMISS_KEY = "decal_lab_incog_dismissed_session";

export function rememberDismiss() {
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {
    // private mode might block it — that's fine, modal won't reappear this view
  }
}

export function wasDismissedThisSession() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}
